import * as React from 'react';
import {
  Box,
  Typography,
  Chip,
  Card,
  CardContent,
  FormControlLabel,
  RadioGroup
} from '@material-ui/core';
import { ThemeProvider } from '@material-ui/core/styles';
import { ValidatorForm } from 'react-material-ui-form-validator';
import { ApplicationContext } from '../../context/Contexts';
import { BuildTheme } from '../../theme';
import {
  CustomInputText,
  CustomFormLabel,
  CustomButton,
  CustomCheckbox,
  CustomRadio,
  CustomSwitch,
  CustomAutocomplete
} from '../../custom-forms';

/**
 * Pagina de test pentru theme-fleet (izolata) — verifica vizual ca tema si atomii
 * s-au copiat corect din ClientApp2. Ruta: /fleet-theme-test (utilizator autentificat).
 */
const SWATCHES: Array<[string, string]> = [
  ['primary', '#37AFAD'],
  ['primary dark', '#00B9C0'],
  ['secondary', '#FF7345'],
  ['secondary dark', '#F48B6C'],
  ['success', '#13DEB9'],
  ['error', '#FA896B'],
  ['warning', '#FFAE1F'],
  ['text', '#000000'],
  ['grey300 / border', '#DFE5EF'],
  ['grey400', '#7C8FAC']
];

const QUALITY = [
  { value: 'OE', name: 'OE' },
  { value: 'OEM', name: 'OEM' },
  { value: 'AM', name: 'AM' }
];

const Section: React.FC<{ title: string }> = ({ title, children }) => (
  <Card style={{ marginBottom: 22 }}>
    <CardContent>
      <Typography
        style={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          color: '#5A6A85',
          marginBottom: 16
        }}
      >
        {title}
      </Typography>
      {children}
    </CardContent>
  </Card>
);

const Row: React.FC = ({ children }) => (
  <Box display="flex" style={{ gap: 14, flexWrap: 'wrap', alignItems: 'center', marginBottom: 14 }}>
    {children}
  </Box>
);

const FleetThemeTestPage: React.FC = () => {
  const theme = React.useMemo(() => BuildTheme(), []);
  const { translatorService } = React.useContext(ApplicationContext);
  const t = React.useCallback(
    (tag: string, def: string) => translatorService.Tranlate(tag, def),
    [translatorService]
  );
  const [radio, setRadio] = React.useState('v2');
  const [checked, setChecked] = React.useState(true);
  const [sw, setSw] = React.useState(true);
  const [cod, setCod] = React.useState('');
  const [quality, setQuality] = React.useState<{ value: string; name: string } | null>(null);
  const [submitted, setSubmitted] = React.useState(false);

  return (
    <ThemeProvider theme={theme}>
      <Box style={{ background: '#EEF1F6', minHeight: '100vh', padding: 32, fontFamily: 'Montserrat' }}>
        <Box style={{ maxWidth: 1040, margin: '0 auto' }}>
          <Typography variant="h3" style={{ marginBottom: 4 }}>
            theme-fleet — pagină de test
          </Typography>
          <Typography style={{ color: '#5A6A85', fontSize: 13, marginBottom: 28 }}>
            Aspect ClientApp2 (MUI v4) · Montserrat · teal #37AFAD / orange #FF7345 · validatori din ClientApp · izolat prin ThemeProvider
          </Typography>

          <Section title="Paletă">
            <Box display="flex" style={{ gap: 12, flexWrap: 'wrap' }}>
              {SWATCHES.map(([name, hex]) => (
                <Box
                  key={name}
                  style={{ width: 150, border: '1px solid #E5EAEF', borderRadius: 8, overflow: 'hidden' }}
                >
                  <Box style={{ height: 50, background: hex }} />
                  <Box style={{ padding: '7px 9px', background: '#fff' }}>
                    <Typography style={{ fontSize: 11.5, fontWeight: 600 }}>{name}</Typography>
                    <Typography style={{ fontSize: 11, color: '#5A6A85' }}>{hex}</Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Section>

          <Section title="Butoane">
            <Row>
              <CustomButton color="primary" size="small">Salvează</CustomButton>
              <CustomButton color="primary">Salvează</CustomButton>
              <CustomButton color="primary" size="large">Salvează calculația</CustomButton>
              <CustomButton color="primary" disabled>Disabled</CustomButton>
            </Row>
            <Row>
              <CustomButton size="small">Trimite</CustomButton>
              <CustomButton>Trimite oferta</CustomButton>
              <CustomButton size="large">Trimite selecția</CustomButton>
              <Typography style={{ fontSize: 11.5, color: '#7C8FAC' }}>contained brand (secondary)</Typography>
            </Row>
            <Row>
              <CustomButton variant="outlined" color="primary" size="small">Selecții optime</CustomButton>
              <CustomButton variant="outlined" color="primary">Cea mai bună calitate</CustomButton>
              <CustomButton variant="outlined" color="primary" size="large">Cel mai bun preț</CustomButton>
              <Typography style={{ fontSize: 11.5, color: '#7C8FAC' }}>outlined + color primary</Typography>
            </Row>
            <Row>
              <CustomButton variant="outlined" size="small">Renunță</CustomButton>
              <CustomButton variant="outlined">Renunță</CustomButton>
              <CustomButton variant="outlined" size="large">Închide</CustomButton>
              <Typography style={{ fontSize: 11.5, color: '#7C8FAC' }}>outlined neutru (fără color)</Typography>
            </Row>
          </Section>

          <ValidatorForm onSubmit={() => setSubmitted(true)} instantValidate>
            <Section title="Input-uri + validări (required)">
              <Row>
                <Box style={{ minWidth: 230 }}>
                  <CustomFormLabel htmlFor="cod">{`${t('ALT_CALC_COL_CODE', 'Cod piesă')} *`}</CustomFormLabel>
                  <CustomInputText
                    id="cod"
                    name="cod"
                    value={cod}
                    onChange={(e: any) => setCod(e.target.value)}
                    placeholder={t('ALT_CALC_CODE_PLACEHOLDER', 'ex. 2GA823031A')}
                    validators={['required']}
                    errorMessages={[t('VALIDATORS_REQUIRED', 'Campul este obligatoriu')]}
                    fullWidth
                  />
                </Box>
                <Box style={{ minWidth: 230 }}>
                  <CustomFormLabel htmlFor="den">{t('ALT_CALC_COL_NAME', 'Denumire')}</CustomFormLabel>
                  <CustomInputText
                    id="den"
                    placeholder={t('ALT_CALC_NAME_PLACEHOLDER', 'ex. Capotă motor')}
                    fullWidth
                  />
                </Box>
                <Box style={{ minWidth: 240, alignSelf: 'flex-start' }}>
                  <CustomAutocomplete
                    label="Calitate *"
                    name="calitate"
                    options={QUALITY}
                    value={quality}
                    getOptionLabel={(o: any) => o?.name ?? ''}
                    onChange={(_e: any, v: any) => setQuality(v)}
                    validators={['required']}
                    errorMessages={[t('VALIDATORS_REQUIRED', 'Campul este obligatoriu')]}
                  />
                </Box>
              </Row>
              <Row>
                <Box style={{ flex: 1, minWidth: 480 }}>
                  <CustomFormLabel htmlFor="msg">
                    {t('ALT_CALC_MESSAGE_LABEL', 'Mesaj pentru service')}
                  </CustomFormLabel>
                  <CustomInputText
                    id="msg"
                    multiline
                    rows={3}
                    placeholder={t(
                      'ALT_CALC_MESSAGE_PLACEHOLDER',
                      'Ex.: Bușonul a fost înlocuit la revizia anterioară.'
                    )}
                    fullWidth
                  />
                </Box>
              </Row>
            </Section>

            <Section title="Selectoare">
              <Row>
                <FormControlLabel
                  control={<CustomCheckbox checked={checked} onChange={(e) => setChecked(e.target.checked)} />}
                  label="Selectează varianta"
                />
                <FormControlLabel
                  control={<CustomSwitch checked={sw} onChange={(e) => setSw(e.target.checked)} />}
                  label="Recomandat"
                />
              </Row>
              <RadioGroup row value={radio} onChange={(e) => setRadio(e.target.value)}>
                <FormControlLabel value="v1" control={<CustomRadio color="secondary" />} label="OE — 134,00 RON" />
                <FormControlLabel value="v2" control={<CustomRadio color="secondary" />} label="OEM — 112,40 RON" />
                <FormControlLabel value="v3" control={<CustomRadio color="secondary" />} label="AM — 89,90 RON" />
              </RadioGroup>
            </Section>

            <Box display="flex" style={{ gap: 12, alignItems: 'center' }}>
              <CustomButton type="submit">Validează</CustomButton>
              {submitted && (
                <Typography style={{ color: '#0C6B50', fontWeight: 600 }}>Formular valid ✓</Typography>
              )}
            </Box>
          </ValidatorForm>

          <Box style={{ height: 22 }} />

          <Section title="Chip-uri / badge-uri">
            <Row>
              <Chip label="OE" size="small" />
              <Chip label="OEM" size="small" style={{ background: '#EBF3FE', color: '#3B57C9' }} />
              <Chip label="AM" size="small" style={{ background: '#E6FFFA', color: '#0E7A54' }} />
              <Chip label="★ Recomandat de service" size="small" color="secondary" />
              <Chip label="Non-stoc" size="small" style={{ background: '#FEF5E5', color: '#C97A12' }} />
            </Row>
          </Section>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default FleetThemeTestPage;
